import { useEffect, useState, useMemo } from "react";
import { api, formatINR, downloadXlsx } from "@/lib/api";
import { Loader2, Plus, X, Check, AlertTriangle, Clock, Calendar, FileCheck2, Building2, Edit2, Trash2, Sparkles, FileSpreadsheet } from "lucide-react";
import { Modal, In } from "@/pages/Workers";

const STATUS_COLOR = {
  overdue: { bg: "#FADED7", color: "#9A3412", icon: AlertTriangle, label: "Overdue" },
  due_soon: { bg: "#FCE7C9", color: "#9A6020", icon: Clock, label: "Due Soon" },
  upcoming: { bg: "var(--color-primary-light)", color: "var(--color-primary)", icon: Calendar, label: "Upcoming" },
};

const CAT_COLOR = {
  "GST": "#1B4332",
  "TDS / Income Tax": "#457B9D",
  "PF / ESI": "#E07A5F",
  "Professional Tax": "#F4A261",
  "Labour": "#3D405B",
  "Licenses": "#81B29A",
  "Environment": "#588157",
  "Agri Specific": "#D4A373",
  "Corporate (ROC)": "#9D4EDD",
  "POSH": "#BC4749",
  "Other": "#57534E",
};

export default function Compliance() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (status) params.status = status;
    const [a, b, c] = await Promise.all([
      api.get("/compliance", { params }),
      api.get("/compliance/summary"),
      api.get("/compliance/categories"),
    ]);
    setItems(a.data); setSummary(b.data);
    setCategories(c.data.categories); setFrequencies(c.data.frequencies);
    setLoading(false);
  };
  useEffect(() => { load(); }, [category, status]);

  const remove = async (id) => {
    if (!window.confirm("Delete this compliance entry?")) return;
    await api.delete(`/compliance/${id}`); load();
  };

  const seed = async () => {
    setSeeding(true);
    const { data } = await api.post("/admin/seed-compliance");
    setSeeding(false);
    alert(`Seeded ${data.added} new compliance tasks. Total: ${data.total_in_db}.`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Statutory & Regulatory</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Compliance Calendar</h1>
          <p className="text-stone-600 mt-1 text-sm">GST · TDS · PF/ESI · PT · Labour · Licenses · Environment · Agri-specific · ROC · POSH</p>
        </div>
        <div className="flex items-center gap-2">
          {items.length === 0 && (
            <button onClick={seed} disabled={seeding} data-testid="seed-compliance-button"
                    className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-secondary)" }}>
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Seed Mandatory Tasks
            </button>
          )}
          <button onClick={() => downloadXlsx("/compliance/export.xlsx", "compliance_register.xlsx")}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium"
                  style={{ background: "#1B4332" }} data-testid="export-compliance-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-compliance-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> Add Compliance
          </button>
        </div>
      </header>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="compliance-kpis">
          <Kpi label="Total Tracked" v={summary.total} accent="#1B4332" />
          <Kpi label="Overdue" v={summary.overdue} accent="#9A3412" bg="#FADED7" />
          <Kpi label="Due in 30 days" v={summary.due_in_30_days} accent="#9A6020" bg="#FCE7C9" />
          <Kpi label="Categories" v={summary.by_category.length} accent="#457B9D" bg="#DCE9F0" />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="compliance-category-filter"
                className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white">
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <div className="flex gap-2">
          {["", "overdue", "due_soon", "upcoming"].map((s) => (
            <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`filter-${s || "all"}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${status === s ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-700"}`}>
              {s ? STATUS_COLOR[s].label : "All"}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        items.length === 0 ? (
          <div className="card-flat text-center py-12">
            <FileCheck2 className="w-12 h-12 mx-auto text-stone-300 mb-3" />
            <h3 className="font-heading text-lg font-bold">No compliance tasks yet</h3>
            <p className="text-sm text-stone-500 mt-1">Click "Seed Mandatory Tasks" above to load 36 essential compliance items for an Indian agri company.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="compliance-grid">
            {items.map((c) => {
              const s = STATUS_COLOR[c.status];
              const catC = CAT_COLOR[c.category] || "#57534E";
              return (
                <div key={c.id} className="card-flat" data-testid={`compliance-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="status-pill" style={{ background: `${catC}15`, color: catC, fontSize: 10 }}>
                          {c.category}
                        </span>
                        <span className="text-[10px] tracking-widest text-stone-500">{c.frequency.toUpperCase()}</span>
                      </div>
                      <div className="font-heading font-semibold text-sm leading-tight">{c.title}</div>
                      {c.authority && <div className="text-xs text-stone-500 mt-1 flex items-center gap-1"><Building2 className="w-3 h-3" />{c.authority}</div>}
                    </div>
                    <span className="status-pill capitalize" style={{ background: s.bg, color: s.color, fontSize: 10 }}>
                      <s.icon className="w-3 h-3" /> {s.label}
                    </span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="kpi-label">Next Due</div>
                        <div className="font-semibold text-sm mt-0.5" style={{ color: c.status === "overdue" ? "#9A3412" : "var(--text-primary)" }}>
                          {new Date(c.due_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </div>
                      </div>
                      {c.last_filed_date && (
                        <div className="text-right">
                          <div className="kpi-label">Last Filed</div>
                          <div className="text-xs mt-0.5 text-stone-600">{c.last_filed_date}</div>
                        </div>
                      )}
                    </div>
                    {c.penalty_note && (
                      <div className="mt-2 text-[11px] text-stone-500 italic line-clamp-2">⚠ {c.penalty_note}</div>
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-stone-100 flex gap-2">
                    <button onClick={() => setCompleting(c)} data-testid={`complete-${c.id}`}
                            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-white text-xs font-medium"
                            style={{ background: "var(--color-primary)" }}>
                      <Check className="w-3.5 h-3.5" /> Mark Filed
                    </button>
                    <button onClick={() => setEditing(c)} className="p-1.5 rounded hover:bg-stone-100"><Edit2 className="w-4 h-4 text-stone-500" /></button>
                    <button onClick={() => remove(c.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {(showAdd || editing) && (
        <ComplianceForm initial={editing} categories={categories} frequencies={frequencies}
                        onClose={() => { setShowAdd(false); setEditing(null); }}
                        onSaved={() => { setShowAdd(false); setEditing(null); load(); }} />
      )}
      {completing && (
        <CompleteForm item={completing} onClose={() => setCompleting(null)} onSaved={() => { setCompleting(null); load(); }} />
      )}
    </div>
  );
}

function Kpi({ label, v, accent, bg }) {
  return (
    <div className="card-flat" style={{ background: bg || "white", padding: 16 }}>
      <div className="kpi-label">{label}</div>
      <div className="font-heading text-2xl font-bold mt-1" style={{ color: accent || "var(--text-primary)" }}>{v}</div>
    </div>
  );
}

function ComplianceForm({ initial, categories, frequencies, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    title: "", category: "GST", frequency: "Monthly",
    due_date: new Date().toISOString().slice(0, 10),
    responsible_person: "", authority: "", amount: 0,
    reminder_days_before: 7, penalty_note: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const body = { ...form, amount: Number(form.amount), reminder_days_before: Number(form.reminder_days_before) };
    if (initial) await api.put(`/compliance/${initial.id}`, body);
    else await api.post("/compliance", body);
    setBusy(false); onSaved();
  };
  return (
    <Modal title={initial ? "Edit Compliance" : "Add Compliance"} onClose={onClose} testid="compliance-modal">
      <form onSubmit={submit} className="space-y-3">
        <In label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} required />
        <div className="grid grid-cols-2 gap-3">
          <label><div className="kpi-label mb-1.5">Category</div>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label><div className="kpi-label mb-1.5">Frequency</div>
            <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
              {frequencies.map((f) => <option key={f}>{f}</option>)}
            </select>
          </label>
          <In label="Next Due Date" type="date" v={form.due_date} on={(v) => setForm({ ...form, due_date: v })} required />
          <In label="Authority" v={form.authority} on={(v) => setForm({ ...form, authority: v })} placeholder="e.g. GSTN, EPFO, MCA" />
          <In label="Responsible Person" v={form.responsible_person} on={(v) => setForm({ ...form, responsible_person: v })} />
          <In label="Reminder (days before)" type="number" v={form.reminder_days_before} on={(v) => setForm({ ...form, reminder_days_before: v })} />
        </div>
        <In label="Amount / Fee (₹, if applicable)" type="number" v={form.amount} on={(v) => setForm({ ...form, amount: v })} />
        <label><div className="kpi-label mb-1.5">Penalty for Delay</div>
          <textarea rows="2" value={form.penalty_note} onChange={(e) => setForm({ ...form, penalty_note: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
        </label>
        <label><div className="kpi-label mb-1.5">Notes</div>
          <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
        </label>
        <button type="submit" disabled={busy} data-testid="save-compliance-button"
                className="w-full py-2 mt-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </Modal>
  );
}

function CompleteForm({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    filed_on: new Date().toISOString().slice(0, 10),
    reference_no: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post(`/compliance/${item.id}/complete`, form);
    setBusy(false); onSaved();
  };
  return (
    <Modal title={`Mark Filed · ${item.title}`} onClose={onClose} testid="complete-compliance-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="text-xs text-stone-500">Due: {item.due_date} · Frequency: {item.frequency}</div>
        <In label="Filed On" type="date" v={form.filed_on} on={(v) => setForm({ ...form, filed_on: v })} required />
        <In label="Reference / Challan No." v={form.reference_no} on={(v) => setForm({ ...form, reference_no: v })} />
        <label><div className="kpi-label mb-1.5">Notes</div>
          <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm" />
        </label>
        <div className="text-xs text-stone-500">
          Next occurrence will be auto-scheduled based on the <b>{item.frequency}</b> cycle.
        </div>
        <button type="submit" disabled={busy} data-testid="confirm-complete-button"
                className="w-full py-2 mt-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Saving…" : "Confirm Filing"}
        </button>
      </form>
    </Modal>
  );
}
